import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark" | "system";

export interface ThemeContextValue {
  /** The user's preference, including "system". */
  theme: Theme;
  /** What is actually applied right now. */
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

/** Default localStorage key — keep in sync with any pre-paint script in index.html. */
export const THEME_STORAGE_KEY = "um.theme";

const Ctx = createContext<ThemeContextValue | null>(null);

const isTheme = (v: unknown): v is Theme =>
  v === "light" || v === "dark" || v === "system";

const prefersDarkQuery = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

const readStored = (storageKey: string): Theme | null => {
  try {
    const v = window.localStorage.getItem(storageKey);
    return isTheme(v) ? v : null;
  } catch {
    return null; // storage unavailable (SSR, privacy mode)
  }
};

export interface ThemeProviderProps {
  children: ReactNode;
  /** Used when no preference is stored yet. Default "system". */
  defaultTheme?: Theme;
  /** localStorage key for persistence. Default {@link THEME_STORAGE_KEY}. */
  storageKey?: string;
}

/**
 * Applies the `.dark` class from styles.css to <html> and persists the
 * user's choice. "system" follows `prefers-color-scheme` live.
 */
export const ThemeProvider = ({
  children,
  defaultTheme = "system",
  storageKey = THEME_STORAGE_KEY,
}: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<Theme>(
    () => readStored(storageKey) ?? defaultTheme
  );
  const [systemDark, setSystemDark] = useState<boolean>(
    () => prefersDarkQuery()?.matches ?? false
  );

  // Follow OS-level changes while in "system" mode.
  useEffect(() => {
    const mql = prefersDarkQuery();
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: "light" | "dark" =
    theme === "system" ? (systemDark ? "dark" : "light") : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolvedTheme === "dark");
    // Native form controls, scrollbars, etc.
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // storage unavailable — theme still applies for this session
      }
    },
    [storageKey]
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme must be used inside <ThemeProvider>");
  return v;
};
